import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';

import { ResponsiveModal } from '../common';
import announcementApi, { Announcement } from '../../services/announcementApi';
import Gate from '../access/Gate';
import PushPreview from './PushPreview';

interface Props {
  announcement: Announcement | null;
  onClose: () => void;
  onChanged: () => void;
}

const STATUS_FA: Record<string, string> = {
  draft: 'پیش‌نویس',
  scheduled: 'زمان‌بندی‌شده',
  sending: 'در حال ارسال',
  sent: 'ارسال شد',
  cancelled: 'لغو شده',
  failed: 'ناموفق',
};

const AUDIENCE_FA: Record<string, string> = {
  all: 'همهٔ کاربران',
  buyers: 'خریداران',
  sellers: 'فروشندگان',
  admins: 'مدیران',
  specific: 'کاربران مشخص',
  segment: 'بخش‌بندی سفارشی',
};

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('fa-IR');

const jalali = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const AnnouncementDetail: React.FC<Props> = ({ announcement, onClose, onChanged }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [live, setLive] = useState<Announcement | null>(announcement);
  const [busy, setBusy] = useState(false);

  useEffect(() => setLive(announcement), [announcement]);

  // Counters only move server-side, so refresh while a run is in flight.
  useEffect(() => {
    if (!live || live.status !== 'sending') return;
    const timer = setInterval(async () => {
      const res = await announcementApi.getOne(live._id);
      if (res.success && res.data) setLive(res.data);
    }, 4000);
    return () => clearInterval(timer);
  }, [live]);

  if (!live) return null;

  const s = live.stats || ({} as Announcement['stats']);
  const progress = s.targeted ? Math.min(100, (s.delivered / s.targeted) * 100) : 0;
  const sendable = ['draft', 'scheduled'].includes(live.status);

  const handleSend = async () => {
    setBusy(true);
    const res = await announcementApi.send(live._id);
    setBusy(false);
    enqueueSnackbar(res.message || (res.success ? 'ارسال آغاز شد' : 'خطا'), {
      variant: res.success ? 'success' : 'error',
    });
    if (res.success) {
      onChanged();
      onClose();
    }
  };

  const metrics = [
    { label: 'مخاطب هدف', value: s.targeted, color: theme.palette.text.primary },
    { label: 'اعلان تحویل‌شده', value: s.delivered, color: theme.palette.success.main },
    { label: 'پوش ارسال‌شده', value: s.pushSent, color: theme.palette.primary.main },
    { label: 'پوش ناموفق', value: s.pushFailed, color: theme.palette.error.main },
  ];

  return (
    <ResponsiveModal
      open={!!announcement}
      onClose={onClose}
      title={live.title}
      subtitle={`${STATUS_FA[live.status] ?? live.status} • ${AUDIENCE_FA[live.audience?.type] ?? '—'}`}
      maxWidth="md"
      loading={busy}
      actions={
        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          <Button onClick={onClose}>بستن</Button>
          <Box sx={{ flex: 1 }} />
          {sendable && (
            <Gate permission="admin_announcement_send">
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => void handleSend()}
                disabled={busy}
              >
                ارسال کن
              </Button>
            </Gate>
          )}
        </Stack>
      }
    >
      <Stack spacing={2}>
        {live.status === 'failed' && live.error && (
          <Alert severity="error">{live.error}</Alert>
        )}

        {live.status === 'sending' && (
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption">در حال ارسال…</Typography>
              <Typography variant="caption" fontWeight={700}>
                {fmt(s.delivered)} / {fmt(s.targeted)}
              </Typography>
            </Stack>
            <LinearProgress
              variant={s.targeted ? 'determinate' : 'indeterminate'}
              value={progress}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        <Grid container spacing={1.5}>
          {metrics.map((m) => (
            <Grid item xs={6} md={3} key={m.label}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {m.label}
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: m.color }}>
                  {fmt(m.value)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PushPreview title={live.title} message={live.message} imageUrl={live.imageUrl} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                مشخصات
              </Typography>
              <Stack spacing={0.75}>
                {[
                  ['دسته', live.category],
                  [
                    'کانال‌ها',
                    [live.channels?.inApp && 'درون‌برنامه', live.channels?.push && 'پوش']
                      .filter(Boolean)
                      .join(' + ') || '—',
                  ],
                  ['مقصد', live.linkUrl || '—'],
                  ['زمان‌بندی', live.scheduledAt ? jalali(live.scheduledAt) : 'بلافاصله'],
                  ['شروع ارسال', jalali(live.startedAt)],
                  ['پایان', jalali(live.completedAt)],
                  [
                    'سازنده',
                    typeof live.createdBy === 'object' ? live.createdBy?.name || '—' : '—',
                  ],
                ].map(([label, value]) => (
                  <Stack key={String(label)} direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'left' }}>
                      {value}
                    </Typography>
                  </Stack>
                ))}
                {live.force && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label="تنظیمات کاربر نادیده گرفته شد"
                    />
                  </>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
            متن اطلاعیه
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 2 }}>
            {live.message}
          </Typography>
        </Paper>
      </Stack>
    </ResponsiveModal>
  );
};

export default AnnouncementDetail;
